import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { width } from '@mui/system';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import useWindow from 'hooks/useWindow';
import Cookies from 'js-cookie';
import { useContext, useEffect, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { IoInformationCircle } from 'react-icons/io5';
import { RxHamburgerMenu } from 'react-icons/rx';
import { useNavigate } from 'react-router-dom';
import { logout } from 'services/auth/auth.service';
import { clearCookies } from 'utils/sessions/cookie.utils';
import styles from './PrivacyPopup.module.css';

export const PrivacyPopup = ({
  hasAcceptedPolicy = false,
  showFull,
  setShowFull,
  setMobileNavOpen,
  policyData,
  setHasFetchedPerms,
  setCookiesAgreed,
  setPrivacyAgreed,
  setNewTermsFullyAgreed,
  setTermsFullyViewed,
  setHasSignedIn,
}) => {
  const navigate = useNavigate();
  const isMsalAuthenticated = useIsAuthenticated();
  const authContext = useContext(AuthContext);
  const { isAuthenticated, isInitialized, logout: authLogout } = authContext;
  const { instance } = useMsal();
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(showFull);
  const [headerInView, setHeaderInView] = useState(true);
  const { width } = useWindow();

  const acceptAction = () => {
    setCookiesAgreed(true);
    setPrivacyAgreed(true);
    if (policyData?.version) {
      Cookies.set('policyAgreed', policyData.version, {
        expires: 365,
      });
    }
    Cookies.set('cookiesAgreed', 'true', { expires: 365 });
    Cookies.set('privacyAgreed', 'true', { expires: 365 });
  };

  const declineAction = () => {
    if (isAuthenticated) {
      logout();
      authLogout();
    }
    setHasFetchedPerms(false);
    setCookiesAgreed(false);
    Cookies.remove('cookiesAgreed');
    setPrivacyAgreed(false);
    Cookies.remove('privacyAgreed');
    Cookies.remove('policyAgreed');
    setNewTermsFullyAgreed(false);
    setTermsFullyViewed(false);
    setHasSignedIn(false);
    clearCookies();
  };

  useEffect(() => {
    if (showFull) {
      setShouldRender(true);
      setIsAnimating(false);
    } else if (shouldRender) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsAnimating(false);
      }, 400); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [showFull, shouldRender]);

  useEffect(() => {
    setShowFull(!hasAcceptedPolicy);
  }, [hasAcceptedPolicy, setShowFull]);

  useEffect(() => {
    if (width > 992) {
      if (!headerInView) setHeaderInView(true);
      return;
    }

    const header = document.getElementById('mobile-header');

    if (!header) return;

    const observer = new IntersectionObserver(([entry]) => setHeaderInView(entry.isIntersecting), {
      threshold: 0.01,
    });

    observer.observe(header);

    return () => observer.disconnect();
  }, [width]);

  const declineHandler = () => {
    if (isMsalAuthenticated) {
      instance.logoutRedirect({
        onRedirectNavigate: (url) => {
          return false;
        },
      });
    }
    declineAction();
    clearCookies();
    navigate('/');
  };
  const acceptHandler = () => {
    acceptAction();
    setShowFull(false);
  };

  return (
    <>
      {shouldRender ? (
        <div
          className={`position-fixed bottom-0 start-0 w-100 ${
            isAnimating ? styles.slideDown : styles.slideUp
          }`}
          style={{ zIndex: 'var(--z-chrome)' }}
        >
          <Container fluid className="px-0">
            <Row className={`gx-0 align-items-stretch bg-primary-highlight ${styles.banner}`}>
              <Col
                xs={12}
                lg={8}
                className="p-3 p-md-4 d-flex flex-column align-items-center text-center gap-3"
              >
                <div className="d-flex align-items-center justify-content-center">
                  <IoInformationCircle size={32} />
                </div>
                <div className="d-flex flex-column gap-2 align-items-center">
                  <h2 className="fw-bold fs-5 mb-0">Privacy &amp; Cookie Policy</h2>
                  <p className="fs-6 mb-0 lh-base">
                    This website uses cookies to ensure you get the best experience on our website.
                    By accessing or using the service, you indicate consent to the following
                    policies:
                  </p>
                  <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
                    <a
                      href={`/terms`}
                      target="_blank"
                      rel="noreferrer"
                      className={`fs-6 text-decoration-underline ${styles.popupLink}`}
                    >
                      Terms of Use
                    </a>
                    <a
                      href={`/policy`}
                      target="_blank"
                      rel="noreferrer"
                      className={`fs-6 text-decoration-underline ${styles.popupLink}`}
                    >
                      Privacy &amp; Cookie Policy
                    </a>
                  </div>
                </div>
              </Col>
              <Col
                xs={12}
                lg={4}
                className="d-flex flex-column flex-md-row flex-lg-column align-items-center text-center gap-2 p-3 p-md-4 justify-content-center"
              >
                <Button
                  variant="light"
                  className={`w-100 ${styles.acceptButton}`}
                  onClick={acceptHandler}
                >
                  Accept &amp; Allow Cookies
                </Button>
                <Button className={`w-100 ${styles.declineButton}`} onClick={declineHandler}>
                  Decline
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      ) : (
        <div
          className={`position-fixed bottom-0 start-0 m-3 d-flex flex-column align-items-center gap-2 rounded-pill bg-primary-highlight shadow px-2 py-2 ${styles.compactPanel}`}
          style={{ zIndex: 'var(--z-chrome)' }}
        >
          {!headerInView && (
            <Button
              type="button"
              className={`btn-transparent px-1 ${styles.iconButton}`}
              aria-label="Open menu"
              onClick={() =>
                setMobileNavOpen?.((prev) => (typeof prev === 'boolean' ? !prev : true))
              }
            >
              <RxHamburgerMenu size={26} />
            </Button>
          )}
          <Button
            type="button"
            className={`btn-transparent p-0 m-0 ${styles.iconButton}`}
            aria-label="Open terms and privacy info"
            onClick={() => setShowFull(true)}
          >
            <IoInformationCircle size={28} />
          </Button>
        </div>
      )}
    </>
  );
};
